<template>
  <div class="filter" :class="`filter--${attribute.key}`">
    <div class="placeholders" v-if="$store.state.view.isLoading">
      <content-placeholders v-for="i in 8" :key="`placeholder-${i}`">
        <content-placeholders-text :lines="1" />
      </content-placeholders>
    </div>

    <template v-if="!$store.state.view.isLoading">
      <div class="filter__dropdown" v-show="!isCollapsed">
        <select 
          id="filters"
          ref="choicesSelect"
          multiple=""
        ></select>

        <span class="filter__count" v-if="selectedCount">
          {{ selectedCount }}
        </span>
      </div>
    </template>
  </div>
</template>


<script>
import Choices from "choices.js";
import "choices.js/public/assets/styles/choices.min.css";

export default {
  name: "AttributeFilter",
  props: {
    attribute: Object,
    hasActiveFilters: Boolean
  },
  data() {
    return {
      manuallyCollapsed: null,
      selectedCount: this.getSelectedCount(),
      choicesInstance: null
    };
  },
  computed: {
    isCollapsed() {
      if (this.manuallyCollapsed !== null) {
        return this.manuallyCollapsed;
      }
      if (
        this.$store.state.filters.current.product_cat !== undefined &&
        this.$store.state.filters.current.product_cat.length
      ) {
        return this.$store.state.filters.current.product_cat.some(termId => {
          const selectedCategory = this.$store.state.categories.find(
            category => category.term_id === termId
          );
          if (selectedCategory && selectedCategory.collapsed !== null) {
            return selectedCategory.collapsed.some(
              collapsedAttributeSlug => `pa_${collapsedAttributeSlug}` === this.attribute.key
            );
          }
          return false;
        });
      }
      return false;
    }
  },
  methods: {
    getChoicesOptions() {
      let options = [];
      for (const option of this.attribute.options) {
        options.push({
          'value': option.slug,
          'label': this.decodeEntityCodes(option.name),
          'selected': this.isOptionSelected(this.attribute.key, option.slug),
          'disabled': option.excluded_filter
        });
      }
      return options;
    },
    resetChoices() {
      this.choicesInstance.setValue([]);
      for (const choiceEl of this.choicesInstance.choiceList.element.querySelectorAll('.is-selected')) {
        choiceEl.classList.remove('is-selected', 'is-highlighted');
      }
      this.selectedCount = 0;
    },
    getSelectedCount() {
      if (this.$store.state.filters.current[this.attribute.key]) {
        return this.$store.state.filters.current[this.attribute.key].length;
      }
      return 0;
    },
    isOptionSelected(attribute, option) {
      return this.$store.state.filters.current[attribute]
              && this.$store.state.filters.current[attribute].includes(option);
    },
    onChoicesOptionClick(event) {
      const clickedValue = event.detail.choice.value;
      if (this.isOptionSelected(this.attribute.key, clickedValue)) {
        this.$store.dispatch("removeFilter", { taxonomy: this.attribute.key, option: clickedValue });
        this.selectedCount -= 1;

        // Hack for deselecting option from choices
        // Choices.js has problems with removing options on "choice" event
        // Remove the option after this event has been done.
        // I needed a looped removal in some cases where 1 second still wasn't enough
        let index = 0;
        const removeValueFunction = function(choicesInstance) {
          let selectedOptions = choicesInstance.getValue();
          let hasOption = false;
          for (const option of selectedOptions) {
            if (option.value === clickedValue) {
              hasOption = true;
            }
          }

          if (hasOption) {
            choicesInstance.removeActiveItemsByValue(clickedValue);
            if (index < 10) {
              index++;
              setTimeout(() => {removeValueFunction(choicesInstance), 1000});
            }
          }
        };
        removeValueFunction(this.choicesInstance);

      } else {
        this.$store.dispatch("addFilter", { taxonomy: this.attribute.key, option: clickedValue });
        this.selectedCount += 1;
      }
    },
    decodeEntityCodes(name) {
      let textArea = document.createElement('textarea');
      textArea.innerHTML = name;
      return textArea.value;
    },
    syncSelectedOptions() {
      const activeFilters = this.$store.state.filters.current[this.attribute.key] || [];
      this.selectedOptions = activeFilters;
    }
  },
  mounted() {
    this.$nextTick(() => {
      // NOTE(joosep): Load attribute options manually to Choices instance.
      // When options where loaded through template <option> tags, it
      // would throw vue error on some category changes because Choices
      // changes the HTML content and vue couldn't find some <option> elements.

      this.choicesInstance = new Choices(this.$refs.choicesSelect, {
        placeholder: true,
        placeholderValue: this.attribute.label || "Select options...",
        searchEnabled: true,
        renderSelectedChoices: 'always',
        choices: this.getChoicesOptions()
      });

      // Event listener for handling "toggle" on option click
      this.$refs.choicesSelect.addEventListener("choice", this.onChoicesOptionClick);
      this.$refs.choicesSelect.addEventListener("hideDropdown", () => {
        for (const item of this.choicesInstance.dropdown.element.querySelectorAll('.is-highlighted')) {
          item.classList.remove('is-highlighted');
        }
      });
    });
  },
  watch: {
    hasActiveFilters: function(value) {
      // If hasActiveFilters turns to false then "clear filters" was pressed
      if (!value && this.selectedCount) this.resetChoices();
    },
    attribute: function(value) {
      if (this.choicesInstance) {
        this.choicesInstance.setChoices(this.getChoicesOptions(), 'value', 'label', true);
      }
    },
  },
  beforeDestroy() {
    if (this.choicesInstance) {
      this.choicesInstance.destroy();
    }
  }
};

</script>
